/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentApiService, PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { ConfirmDialogComponent, NotificationService, UserPreferencesService } from '@alfresco/adf-core';
import { DownloadNodesAction } from '@alfresco/aca-shared/store';
import { NodeEntry } from '@alfresco/js-api';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { FavoritesService } from '../favorites/favorites.service';
import { bulkReport, BulkOutcome, errorSummary, isConflict, runEach } from '../shared/bulk';
import { DmsOption, DmsMetadataService } from '../upload/dms-metadata.service';
import { AllDocumentsService, CountValue, DocumentCounts, DocumentFilters, DocumentRow, FacetOption, SortField } from './all-documents.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const PAGE_SIZES = [20, 50, 100];

const SORTS: { field: SortField; label: string }[] = [
  { field: 'cm:modified', label: 'DMS.UI.69C580' },
  { field: 'cm:created', label: 'DMS.UI.1ECED3' },
  { field: 'cm:name', label: 'DMS.UI.F882A4' }
];

/** File-type badge shown next to the document name. */
const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG'
};

/**
 * All documents (UI-04).
 *
 * A server-paged inventory of the repository with summary counts, filters, a
 * dense table and a details panel. Every number and row comes from an authorised
 * Search API query — see {@link AllDocumentsService} — so nothing here depends on
 * loading the repository into the browser.
 */
@Component({
  selector: 'app-all-documents',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './all-documents.component.html',
  styleUrls: ['./all-documents.component.scss']
})
export class AllDocumentsComponent implements OnInit {
  private readonly docs = inject(AllDocumentsService);
  private readonly translate = inject(TranslateService);
  private readonly dms = inject(DmsMetadataService);
  private readonly content = inject(ContentApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly adfPreferences = inject(UserPreferencesService);
  private readonly favorites = inject(FavoritesService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  readonly pageSizes = PAGE_SIZES;
  readonly sorts = SORTS;

  readonly rows = signal<DocumentRow[]>([]);
  /** Exact number of matches, or `null` when the repository would not give one. */
  readonly total = signal<number | null>(null);
  readonly hasMore = signal(false);
  readonly counts = signal<DocumentCounts | null>(null);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly selected = signal<DocumentRow | null>(null);
  readonly previewFailed = signal(false);
  readonly view = signal<'list' | 'grid'>('list');

  /** Rows ticked for a bulk action — on this page only, as in the trash. */
  readonly checkedIds = signal<Set<string>>(new Set());
  readonly checkedCount = computed(() => this.checkedIds().size);
  readonly allChecked = computed(() => this.rows().length > 0 && this.rows().every((row) => this.checkedIds().has(row.id)));
  readonly someChecked = computed(() => this.checkedCount() > 0 && !this.allChecked());
  readonly working = signal(false);

  /**
   * Moved to the trash in this visit. The search index catches up a little after
   * the repository, so a reload straight away can still list them.
   */
  private readonly trashed = new Set<string>();

  /** Filter choices: constrained values from the model, free values from facets. */
  documentTypes: DmsOption[] = [];
  statuses: DmsOption[] = [];
  departments: FacetOption[] = [];
  creators: FacetOption[] = [];

  filters: DocumentFilters = { documentType: '', department: '', status: '', creator: '', term: '' };

  sortField: SortField = 'cm:modified';
  ascending = false;
  /** Starts from the size chosen in Settings, which ADF's lists share. */
  pageSize = PAGE_SIZES.includes(this.adfPreferences.paginationSize) ? this.adfPreferences.paginationSize : PAGE_SIZES[0];
  pageIndex = 0;

  ngOnInit(): void {
    this.dms.schema().subscribe((schema) => {
      this.documentTypes = schema.fields['dms:documentType']?.options ?? [];
      this.statuses = schema.fields['dms:status']?.options ?? [];
    });

    // the document-type catalogue and the organisation overview link here with a filter pre-selected
    const params = this.route.snapshot.queryParamMap;
    this.filters.documentType = params.get('documentType') ?? '';
    this.filters.status = params.get('status') ?? '';
    this.filters.department = params.get('department') ?? '';

    this.reload();
  }

  // ---- data ---------------------------------------------------------------

  /** Reloads rows, counts and facets for the current filter/sort/page state. */
  reload(): void {
    this.loading.set(true);
    this.failed.set('');

    this.docs.page(this.filters, this.sortField, this.ascending, this.pageIndex * this.pageSize, this.pageSize).subscribe({
      next: (page) => {
        const rows = page.rows.filter((row) => !this.trashed.has(row.id));
        this.rows.set(rows);
        // a tick on a row that is no longer on the page would act on something unseen
        this.checkedIds.update((ids) => new Set([...ids].filter((id) => rows.some((row) => row.id === id))));
        this.total.set(page.total);
        this.hasMore.set(page.hasMoreItems);
        this.loading.set(false);

        // keep the details panel on a row that is still in the result
        const current = this.selected();
        if (current && !rows.some((r) => r.id === current.id)) {
          this.selected.set(null);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.rows.set([]);
        this.hasMore.set(false);
        this.failed.set(this.describe(err));
      }
    });

    this.docs.counts(this.filters).subscribe({
      next: (counts) => this.counts.set(counts),
      // a failed count must not blank the table; the cards simply stay hidden
      error: () => this.counts.set(null)
    });

    this.docs.facets(this.filters).subscribe({
      next: ({ departments, creators }) => {
        this.departments = departments;
        this.creators = creators;
      },
      error: () => {
        this.departments = [];
        this.creators = [];
      }
    });
  }

  /** Any filter change restarts paging — page 4 of the old result is meaningless. */
  applyFilters(): void {
    this.pageIndex = 0;
    this.reload();
  }

  clearFilters(): void {
    this.filters = { documentType: '', department: '', status: '', creator: '', term: '' };
    this.applyFilters();
  }

  get hasFilters(): boolean {
    return !!(this.filters.documentType || this.filters.department || this.filters.status || this.filters.creator || this.filters.term);
  }

  /** Clicking a summary card filters the table by that status. */
  filterByStatus(status: string): void {
    this.filters.status = this.filters.status === status ? '' : status;
    this.applyFilters();
  }

  // ---- sorting, paging, view ---------------------------------------------

  changeSort(field: SortField): void {
    this.sortField = field;
    this.pageIndex = 0;
    this.reload();
  }

  toggleDirection(): void {
    this.ascending = !this.ascending;
    this.reload();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.pageIndex = 0;
    this.reload();
  }

  goToPage(index: number): void {
    if (index < 0 || index === this.pageIndex || (index > this.pageIndex && !this.hasMore())) {
      return;
    }
    this.pageIndex = index;
    this.reload();
  }

  setView(view: 'list' | 'grid'): void {
    this.view.set(view);
  }

  /**
   * Page count, but only when the repository gave an exact total. Paging
   * otherwise runs off `hasMoreItems`, so no page number is ever invented.
   */
  get pageCount(): number | null {
    const total = this.total();
    return total === null ? null : Math.max(1, Math.ceil(total / this.pageSize));
  }

  get canGoNext(): boolean {
    return this.hasMore();
  }

  /** Page numbers around the current page, with gaps rendered as `-1`. */
  get pageNumbers(): number[] {
    const count = this.pageCount;
    if (count === null) {
      return [];
    }
    if (count <= 7) {
      return Array.from({ length: count }, (_, i) => i);
    }

    const pages = new Set<number>([0, count - 1, this.pageIndex]);
    for (const offset of [-1, 1]) {
      const near = this.pageIndex + offset;
      if (near > 0 && near < count - 1) {
        pages.add(near);
      }
    }

    const ordered = [...pages].sort((a, b) => a - b);
    const withGaps: number[] = [];
    ordered.forEach((page, i) => {
      if (i > 0 && page - ordered[i - 1] > 1) {
        withGaps.push(-1);
      }
      withGaps.push(page);
    });
    return withGaps;
  }

  get rangeStart(): number {
    return this.rows().length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get rangeEnd(): number {
    return this.pageIndex * this.pageSize + this.rows().length;
  }

  /** "1,248 รายการ" when the total is exact, "มากกว่า 1,000 รายการ" when it is not. */
  countText(count: CountValue): string {
    const prefix = count.exact ? '' : this.translate.instant('DMS.UI.0020EB');
    return prefix + count.value.toLocaleString('th-TH');
  }

  get sortLabel(): string {
    return SORTS.find((s) => s.field === this.sortField)?.label ?? '';
  }

  // ---- rows ---------------------------------------------------------------

  select(row: DocumentRow): void {
    this.previewFailed.set(false);
    this.selected.set(this.selected()?.id === row.id ? null : row);
  }

  closePanel(): void {
    this.selected.set(null);
  }

  /** Folders navigate the repository; files open the document page. */
  open(row: DocumentRow): void {
    void this.router.navigate(row.isFolder ? ['/personal-files', row.id] : ['/document', row.id]);
  }

  download(row: DocumentRow): void {
    if (!row.isFolder) {
      // the ADF helper appends the auth ticket; a bare API URL would 401
      window.open(this.content.getContentUrl(row.id, true), '_blank');
    }
  }

  /** Ticketed doclib rendition URL, so the `img` request is authenticated. */
  thumbnailUrl(row: DocumentRow): string {
    return this.content.getDocumentThumbnailUrl(row.id);
  }

  /** A document with no rendition yet falls back to its file-type badge. */
  onThumbnailError(): void {
    this.previewFailed.set(true);
  }

  // ---- ticking rows for a bulk action ------------------------------------

  isChecked(row: DocumentRow): boolean {
    return this.checkedIds().has(row.id);
  }

  toggleCheck(row: DocumentRow): void {
    this.checkedIds.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(row.id)) {
        next.add(row.id);
      }
      return next;
    });
  }

  toggleAllChecks(): void {
    this.checkedIds.set(this.allChecked() ? new Set() : new Set(this.rows().map((row) => row.id)));
  }

  clearChecks(): void {
    this.checkedIds.set(new Set());
  }

  private checkedRows(): DocumentRow[] {
    const ids = this.checkedIds();
    return this.rows().filter((row) => ids.has(row.id));
  }

  /** One file downloads as itself; several, or a folder, come as a zip through ADF's dialog. */
  downloadChecked(): void {
    const rows = this.checkedRows();
    if (rows.length) {
      const nodes = rows.map((row) => ({ entry: { id: row.id, name: row.name, isFile: !row.isFolder, isFolder: row.isFolder } }) as NodeEntry);
      this.store.dispatch(new DownloadNodesAction(nodes));
    }
  }

  /** A row that is already a favourite counts as done. */
  favoriteChecked(): void {
    this.runBulk(
      this.checkedRows(),
      (row) => this.favorites.add(row.id, row.isFolder).pipe(catchError((error) => (isConflict(error) ? [null] : throwError(() => error)))),
      'DMS.BULK.FAVORITED',
      () => this.clearChecks()
    );
  }

  /** The trash can give them back, but it is still asked first. */
  trashChecked(): void {
    const rows = this.checkedRows();
    if (!rows.length || this.working()) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.BULK.TRASH_TITLE',
          message: this.translate.instant('DMS.BULK.TRASH_MESSAGE', { count: rows.length }),
          yesLabel: 'DMS.BULK.TRASH',
          noLabel: 'DMS.BULK.CANCEL'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.runBulk(
            rows,
            (row) => this.docs.trash(row.id),
            'DMS.BULK.TRASHED',
            (outcome) => {
              outcome.ok.forEach((id) => this.trashed.add(id));
              this.clearChecks();
              if (outcome.ok.some((id) => this.selected()?.id === id)) {
                this.selected.set(null);
              }
              this.reload();
            }
          );
        }
      });
  }

  private runBulk(rows: DocumentRow[], operation: (row: DocumentRow) => any, action: string, after: (outcome: BulkOutcome) => void): void {
    if (!rows.length || this.working()) {
      return;
    }
    this.working.set(true);
    runEach(rows, operation, (error) => this.reason(error)).subscribe((outcome) => {
      this.working.set(false);
      const report = bulkReport(outcome, this.translate.instant(action));
      const text = this.translate.instant(report.key, report.params);
      if (report.level === 'info') {
        this.notification.showInfo(text);
      } else if (report.level === 'warning') {
        this.notification.showWarning(text);
      } else {
        this.notification.showError(text);
      }
      after(outcome);
    });
  }

  // ---- view helpers -------------------------------------------------------

  label(value: string): string {
    return this.docs.label(value);
  }

  badge(row: DocumentRow): string {
    return row.isFolder ? '' : (BADGES[this.extension(row.name)] ?? 'FILE');
  }

  badgeClass(row: DocumentRow): string {
    return 'aca-docs__badge--' + (row.isFolder ? 'folder' : this.extension(row.name));
  }

  statusClass(status: string): string {
    return 'aca-docs__status--' + (status || 'none');
  }

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || (name?.[0] ?? '?').toUpperCase();
  }

  formatSize(bytes?: number): string {
    if (bytes === undefined || bytes === null) {
      return '—';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  trackById(_: number, row: DocumentRow): string {
    return row.id;
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private reason(error: unknown): string {
    return errorSummary(error) || this.translate.instant('DMS.BULK.UNKNOWN_ERROR');
  }

  private describe(error: any): string {
    return error?.error?.error?.briefSummary || error?.message || 'DMS.UI.DC34FF';
  }
}
